import React, { useEffect, useState } from 'react';
import { Button as ANTButton, Form, Switch, Space, InputNumber } from 'antd';
import Select from 'components/Atom/Select';
import Input from 'components/Atom/Input';
import { Button } from 'components/Atom/Button';
import { PlusCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { addPlan, getAllProducts, getAllPricingModels, updatePlan } from 'api/subscriptions';
import { useSelector } from 'react-redux';
import { SUB_CurrencyNames } from 'utils/services';
import { notification } from 'antd';
import './assets/style.css';

const NAME_VALIDATION_REGEX = /^[^%?{}[\]$^*@<>]*$/;
const NAME_VALIDATION_MESSAGE = '%?{}[]$^*@<> are not allowed.';

const validateName = (_, value) => {
  if (!value) return Promise.resolve();
  if (NAME_VALIDATION_REGEX.test(value?.trim())) return Promise.resolve();
  return Promise.reject(NAME_VALIDATION_MESSAGE);
};

const CreatePlan = ({ handleOpen, refetch, planData, isEdit }) => {
  let init_Product = planData?.product?.map((product) => ({ label: `${product?.name} / ${product?.product_id}`, value: product?.product_id, quantity: product?.quantity }));
  const [products, setProducts] = useState([]);
  const [pricingModelOptions, setPricingModelOptions] = useState([]);
  const [selectedProduct, setSelectedProducts] = useState(init_Product?.map((item) => item?.value) || []);
  const [isUsageBased, setIsUsageBased] = useState(isEdit ? !!planData?.is_usage_based : false);
  const [isPlanAdding, setIsPlanAdding] = useState(false);
  const store = useSelector((state) => state?.company?.store);
  const userStores = useSelector((state) => state?.company?.userStores);
  useEffect(() => {
    (async () => {
      const params = { ...(store?.isSelected && { store_id: store?._id }), active: true, xap_request: true };
      const response = await getAllProducts(params);
      const modifiedData = response?.data?.data?.products?.map((product) => {
        const productObj = {
          label: `${product?.name} / ${product?.product_id}`,
          value: product?.product_id,
        };

        return productObj;
      });
      modifiedData && setProducts(modifiedData);
    })();
  }, [store]);

  useEffect(() => {
    if (isEdit) return;
    (async () => {
      const params = { ...(store?.isSelected && { store_id: store?._id }), offset: 0, limit: 1000, xap_request: true };
      const response = await getAllPricingModels(params);
      const items = response?.data?.data?.pricingModels || [];
      const options = items?.map((model) => ({
        label: `${model?.name} / ${model?.id}`,
        value: model?.id,
      }));
      setPricingModelOptions(options);
    })();
  }, [store, isEdit]);
  const onAddSubmit = async (values) => {
    if (userStores?.length > 1 && !store?.isSelected && !isEdit) {
      notification.error({ message: 'Select store from the drop-down menu above', placement: 'bottomRight' });
      return;
    }
    setIsPlanAdding(true);
    const { name, amount, every, billing_cycle, products, interval, active, description, currency, is_usage_based, pricing_models } = values;
    const productArray = products
      ?.filter((item) => item?.selectedProduct?.value || item?.value)
      .map((item) => ({
        product_id: item?.selectedProduct?.value || item?.value,
        quantity: Number(item?.quantity) || 1,
      }));
    const pricingModelsArray = (pricing_models || [])?.map((modelId) => (typeof modelId === 'string' ? modelId : modelId?.value)?.trim())?.filter(Boolean);

    if (!isEdit && is_usage_based && pricingModelsArray?.length === 0) {
      notification.error({ message: 'At least one pricing model is required when usage based is enabled', placement: 'bottomRight' });
      setIsPlanAdding(false);
      return;
    }

    const usageBasedPayload =
      !isEdit && is_usage_based
        ? {
            is_usage_based: true,
            pricing_models: pricingModelsArray,
          }
        : {};

    const payload = !isEdit
      ? {
          ...(name?.trim() && { name: name?.trim() }),
          ...(amount?.length > 0 && { amount: Number(amount), currency: currency?.value || 'PKR' }),
          every: Number(every),
          active,
          product: productArray,
          interval: interval?.value,
          billing_cycle: billing_cycle?.value,
          description,
          ...usageBasedPayload,
          api_version: 2,
        }
      : {
          ...(name?.trim() && { name: name?.trim() }),
          amount: Number(amount),
          currency: currency?.value || currency,
          every: Number(every),
          active,
          ...(productArray?.length > 0 && { product: productArray }),
          interval: interval?.value || interval,
          billing_cycle: billing_cycle?.value,
          description,
          ...usageBasedPayload,
          api_version: 2,
        };
    const response = isEdit ? await updatePlan(planData?.store_id, planData?.plan_id, payload) : await addPlan(store?._id, payload);
    if (response?.data?.success) {
      handleOpen();
      refetch();
    }
    setIsPlanAdding(false);
  };

  const intervalOptions = [
    {
      label: 'Minute',
      value: 'minute',
    },
    {
      label: 'Day',
      value: 'day',
    },
    {
      label: 'Week',
      value: 'week',
    },
    {
      label: 'Month',
      value: 'month',
    },
    {
      label: 'Year',
      value: 'year',
    },
  ];

  const billingCycleOptions = [
    {
      label: 'per_unit/user',
      value: 'per_unit/user',
    },
  ];
  const initialValues = isEdit
    ? {
        name: planData?.name,
        active: planData?.active,
        description: planData?.description,
        billing_cycle: { value: 'per_unit/user', label: 'per_unit/user' },
        products: init_Product,
        amount: planData?.amount,
        currency: planData?.currency,
        every: planData?.every,
        interval: planData?.interval,
      }
    : {
        product: [],
        is_usage_based: false,
        pricing_models: [],
        billing_cycle: { value: 'per_unit/user', label: 'per_unit/user' },
      };
  return (
    <div className='formDetails'>
      <Form layout='vertical' onFinish={onAddSubmit} initialValues={initialValues}>
        <div className='create-form'>
          <Form.Item name='name' label='Name' className='customInput mobileInputView' rules={[{ validator: validateName }]}>
            <Input type='string' placeholder='Enter name' name='name' />
          </Form.Item>
          <Form.Item
            name='amount'
            label='Price'
            className='customInput mobileInputView'
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\d+(\.\d+)?$/.test(value) && value?.length <= 15) {
                    return Promise.resolve();
                  }
                  if (!/^\d+(\.\d+)?$/.test(value)) {
                    return Promise.reject('Only positive numbers are allowed');
                  }
                  if (value.length > 15) {
                    return Promise.reject('Maximum 15 digits are allowed');
                  }
                  return Promise.resolve();
                },
              },
            ]}>
            <Input type='number' placeholder='Enter Price' name='amount' />
          </Form.Item>

          <Form.Item name='currency' label='Currency' className='customInput mobileInputView'>
            <Select showSearch defaultValue='PKR' optionsValue={SUB_CurrencyNames} />
          </Form.Item>

          <Form.Item
            name='every'
            label='Every'
            className={`customInput mobileInputView ${isEdit ? '' : 'steric-after'}`}
            rules={[
              { required: !isEdit, message: 'Every is required' },
              {
                message: 'Only positive numbers allowed',
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\d+$/.test(value) && Number(value) > 0) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject('Invalid Every');
                  }
                },
              },
            ]}>
            <Input type='number' placeholder='Enter Every' name='every' />
          </Form.Item>
          <Form.Item
            name='interval'
            label='Interval'
            className={`customInput mobileInputView ${isEdit ? '' : 'steric-after'}`}
            rules={!isEdit && [{ required: true, message: 'Interval is required' }]}>
            <Select defaultValue='Select Interval' optionsValue={intervalOptions} />
          </Form.Item>
          <Form.Item
            name='billing_cycle'
            label='Billing Cycle'
            className={`customInput mobileInputView ${isEdit ? '' : 'steric-after'}`}
            rules={!isEdit && [{ required: true, message: 'Billing Cycle is required' }]}>
            <Select defaultValue={'per_unit/user'} optionsValue={billingCycleOptions} />
          </Form.Item>
          <Form.Item
            name='description'
            label='Description'
            className='customInput mobileInputView'
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const regex = /^[^{}[\]$^<>]+$/;
                  if (regex.test(value)) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject('{}[]$^<> are not allowed.');
                  }
                },
              },
            ]}>
            <Input type='string' placeholder='Enter Description' name='description' />
          </Form.Item>
          {!isEdit && (
            <Form.Item name='is_usage_based' label='Usage Based' valuePropName='checked' className='customInput mobileInputView checked-label'>
              <Switch onChange={setIsUsageBased} />
            </Form.Item>
          )}
        </div>
        {!isEdit && isUsageBased && (
          <Form.Item
            name='pricing_models'
            label='Pricing Models'
            className='customInput mobileInputView pricing-models-multiselect steric-after'
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value?.length === 0) {
                    return Promise.reject('At least one pricing model is required');
                  }
                  return Promise.resolve();
                },
              },
            ]}>
            <Select
              mode='multiple'
              placeholder='Select pricing models'
              showSearch
              allowClear
              labelInValue={false}
              maxTagCount={1}
              maxTagTextLength={50}
              maxTagPlaceholder={(omitted) => `+${omitted?.length} more`}
              optionsValue={pricingModelOptions}
            />
          </Form.Item>
        )}
        <Form.List name='products' initialValue={['']}>
          {(fields, { add, remove }) => (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ fontSize: '14px !important', fontWeight: '500', paddingBottom: '4px' }}>Add Product {!isEdit && <span style={{ color: '#ff4d4f', paddingTop: '1px' }}>*</span>}</label>
              </div>
              <div className=''>
                {fields?.map(({ key, name, fieldKey, ...restField }, index) => (
                  <Space key={key} align='baseline' className='feature-Input-div'>
                    <div>
                      <Form.Item
                        {...restField}
                        name={[name, 'selectedProduct']}
                        fieldKey={[fieldKey, 'selectedProduct']}
                        className='customInput mobileInputView product-select'
                        style={{ marginBottom: '0px' }}
                        rules={[{ required: isEdit ? false : true, message: 'Product is required' }]}>
                        <Select
                          placeholder='Select Product'
                          showSearch
                          defaultValue={isEdit ? init_Product?.[index] : undefined}
                          onChange={(value) => {
                            setSelectedProducts((prev) => {
                              prev[index] = value?.value;
                              return [...prev];
                            });
                          }}>
                          {products?.map((prod) => {
                            return (
                              <Option disabled={selectedProduct?.includes(prod?.value)} value={prod?.value}>
                                {prod?.label}
                              </Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'self-start' }} className='minus-icon'>
                      <Form.Item
                        {...restField}
                        name={[name, 'quantity']}
                        fieldKey={[fieldKey, 'quantity']}
                        rules={[
                          {
                            message: 'Only positive numbers allowed',
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              if (/^[0-9]+$/.test(value)) {
                                return Promise.resolve();
                              } else {
                                return Promise.reject('Invalid quantity');
                              }
                            },
                          },
                        ]}>
                        <Input
                          min={1}
                          type='number'
                          defaultValue={isEdit ? Number(init_Product?.[index]?.quantity) : 1}
                          placeholder='Enter Quantity'
                          className='customInput mobileInputView'
                          style={{ height: '36px', width: '100%', borderRadius: '8px' }}
                        />
                      </Form.Item>
                      {fields?.length > 1 ? (
                        <MinusCircleOutlined
                          style={{ fontSize: '18px' }}
                          onClick={() => {
                            remove(name);
                            const currentProduct = [...selectedProduct];
                            if (currentProduct?.length > 0) {
                              const updatedProducts = currentProduct?.filter((_, index) => index !== name);
                              setSelectedProducts(updatedProducts);
                            }
                          }}
                        />
                      ) : null}
                      {name === fields?.length - 1 && <PlusCircleOutlined style={{ paddingTop: '10px', fontSize: '18px' }} onClick={() => add()} />}
                    </div>
                  </Space>
                ))}
              </div>
            </div>
          )}
        </Form.List>
        <Form.Item name='active' label='Active' valuePropName='checked' initialValue={true} className='customInput mobileInputView checked-label'>
          <Switch defaultChecked />
        </Form.Item>
        <div className='mb-[-20px]  form_button update-button'>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'right' }}>
              <ANTButton
                block
                className='rounded-lg'
                onClick={() => {
                  handleOpen();
                }}>
                Cancel
              </ANTButton>
              <Button block className='button-theme ml-4 rounded-lg' htmlType='submit' loading={isPlanAdding}>
                {isEdit ? 'Update' : 'Add'}
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};
export default CreatePlan;
